import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TimeProgressBar } from './models/TimeProgressBar';

@Component({
    selector: 'sera-time-progress-bar',
    templateUrl: './sera-time-progress-bar.component.html',
    styleUrl: './sera-time-progress-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SeraTimeProgressBarComponent implements OnChanges {
    timeOfActivityText: string = 'LABEL.NO_TIME_SET.TXT';
    progressBarMode: 'invalid' | 'disabled' | 'valid' | 'caution' | null | undefined = 'disabled';
    barTooltipText: string = 'LABEL.PARTIAL_TIME_OF_ACTIVITY.TXT';
    partialTimeForTooltip: number = 0;
    timeUnit: string = '';
    infoTone: 'neutral' | 'info' | 'positive' | 'caution' | 'critical' = 'neutral';

    @Input()
    timeProgressBar: TimeProgressBar = {
        progressValue: 0,
        bottomLabel: 'PAGE.ANALYSIS.CRITERION6.ZIELWERT.LBL',
        timeOfActivity: 0,
        maxValueAllowed: 100,
        overridenTitle: null,
        overridenMode: null,
        isOnlyValidIfMaxIsReached: true,
        disableHover: false
    };

    @Input()
    isToHaveTitle: boolean = true;

    @Input()
    isEditMode: boolean | null = false;

    @Input()
    shouldShowTooltip: boolean = true;

    @Input()
    isTimeUnitInSeconds: boolean | null | undefined = true;

    @Input()
    timeOfActivityDecimals: number = 2;

    @Input()
    partialTimeForTooltipDecimals: number = 0;

    @Input()
    percentageDecimals = '1.0-1';

    constructor(private readonly translate: TranslateService) {}

    ngOnChanges() {
        this.progressBarMode = this.checkStateOfProgressBar();
        this.setTimeOfActivityText();
        if (this.timeProgressBar.progressValue && this.timeProgressBar.timeOfActivity) {
            const partialTime = (this.timeProgressBar.progressValue / 100) * this.timeProgressBar.timeOfActivity;
            this.partialTimeForTooltip =
                this.partialTimeForTooltipDecimals === 0 ? Math.round(partialTime) : Number.parseFloat(partialTime.toFixed(this.partialTimeForTooltipDecimals));
        }
        this.timeUnit = this.getTimeUnitText();
        this.infoTone = this.getInfoTone();
    }

    getTimeUnitText() {
        return this.isTimeUnitInSeconds ? 'PAGE.ANALYSIS.CRITERION11.SECONDS.LBL' : 'PAGE.ANALYSIS.TIME_UNIT.MIN.LBL';
    }

    setTimeOfActivityText() {
        if (this.timeProgressBar.timeOfActivity && this.timeProgressBar.timeOfActivity > 0) {
            const timeOfActivity = this.timeProgressBar.timeOfActivity;
            const formattedTime = Number.isInteger(timeOfActivity) ? timeOfActivity.toString() : timeOfActivity.toFixed(this.timeOfActivityDecimals);
            this.timeOfActivityText = formattedTime + ' ' + this.translate.instant(this.getTimeUnitText());
        } else {
            this.timeOfActivityText = this.translate.instant('LABEL.NO_TIME_SET.TXT');
        }
    }

    checkStateOfProgressBar() {
        if (this.timeProgressBar.overridenMode !== undefined && this.timeProgressBar.overridenMode !== null) return this.timeProgressBar.overridenMode;
        if (this.isEditMode) return 'disabled';
        if (this.timeProgressBar.progressValue == null) return 'valid';
        if (this.timeProgressBar.isOnlyValidIfMaxIsReached) {
            return Math.round(this.timeProgressBar.progressValue * 10) / 10 === this.timeProgressBar.maxValueAllowed ? 'valid' : 'invalid';
        } else {
            return Math.round(this.timeProgressBar.progressValue * 10) / 10 > this.timeProgressBar.maxValueAllowed ? 'invalid' : 'valid';
        }
    }

    getInfoTone(): 'neutral' | 'info' | 'positive' | 'caution' | 'critical' {
        if (this.progressBarMode === 'invalid') return 'critical';
        return 'neutral';
    }
}
